import React, { useState, useEffect, useRef, useCallback } from "react";
import "./Hiscores.css";

interface HiscoreEntry {
  rank: number;
  characterName: string;
  accountUsername: string | null;
  fleetTag: string | null;
  titleName: string | null;
  level?: number;
  xp?: number;
  totalLevel?: number;
  totalXp?: number;
  wealth?: number;
}

interface PlayerDetail {
  characterName: string;
  accountUsername: string | null;
  fleetTag: string | null;
  titleName: string | null;
  level: number;
  totalLevel: number;
  totalXp: number;
  wealth: number;
  skills?: Record<string, { level: number; totalXp: number; color?: string }>;
  rank?: number;
}

interface SearchResult {
  characterName: string;
  accountUsername: string | null;
  fleetTag: string | null;
  titleName: string | null;
  level: number;
  totalLevel: number;
  totalXp: number;
  wealth: number;
}

const IMPLEMENTED_SKILLS = [
  "gunnery",
  "tactics",
  "fishing",
  "salvaging",
  "foraging",
  "mining",
  "trading",
];

const CATEGORIES = [
  { id: "overall", label: "Overall", group: "MAIN" },
  { id: "wealth", label: "Wealth", group: "MAIN" },
  { id: "gunnery", label: "Gunnery", group: "COMBAT" },
  { id: "tactics", label: "Tactics", group: "COMBAT" },
  { id: "fishing", label: "Fishing", group: "GATHERING" },
  { id: "salvaging", label: "Salvaging", group: "GATHERING" },
  { id: "foraging", label: "Foraging", group: "GATHERING" },
  { id: "trading", label: "Trading", group: "UTILITY" },
  { id: "mining", label: "Mining", group: "GATHERING" },
];

interface HiscoresPageProps {
  onBackToGame?: () => void;
}

export const HiscoresPage: React.FC<HiscoresPageProps> = ({ onBackToGame }) => {
  const [category, setCategory] = useState("overall");
  const [entries, setEntries] = useState<HiscoreEntry[]>([]);
  const [selectedPlayer, setSelectedPlayer] = useState<PlayerDetail | null>(
    null,
  );
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [playerLoading, setPlayerLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);

  const debounceTimer = useRef<NodeJS.Timeout | null>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const searchContainerRef = useRef<HTMLDivElement>(null);

  const limit = 25;
  const serverUrl = process.env.REACT_APP_API_URL || "http://localhost:3001";

  // Fetch hiscores data on category or page change
  useEffect(() => {
    if (selectedPlayer) return; // Don't fetch table if viewing player overview

    const fetchHiscores = async () => {
      setLoading(true);
      try {
        let url = "";
        if (category === "overall") {
          url = `${serverUrl}/api/hiscores/overall?page=${page}&limit=${limit}`;
        } else if (category === "wealth") {
          url = `${serverUrl}/api/hiscores/wealth?page=${page}&limit=${limit}`;
        } else {
          url = `${serverUrl}/api/hiscores/skill/${category}?page=${page}&limit=${limit}`;
        }

        console.log("🔄 Fetching hiscores from:", url);
        const response = await fetch(url);
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }
        const data = await response.json();
        console.log("✅ Hiscores data received:", data);

        setEntries(data.entries || []);
        setTotalPages(data.totalPages || 1);
        setTotalCount(data.totalCount || 0);
      } catch (error) {
        console.error("❌ Failed to fetch hiscores:", error);
        setEntries([]);
        setTotalPages(1);
        setTotalCount(0);
      } finally {
        setLoading(false);
      }
    };

    fetchHiscores();
  }, [category, page, limit, serverUrl, selectedPlayer]);

  // Debounced search
  const performSearch = useCallback(
    async (query: string) => {
      if (!query.trim() || query.length < 2) {
        setSearchResults([]);
        setShowSearchResults(false);
        return;
      }

      setSearchLoading(true);
      try {
        const url = `${serverUrl}/api/hiscores/search?name=${encodeURIComponent(query)}`;
        console.log("🔍 Searching for players:", url);
        const response = await fetch(url);

        if (!response.ok) {
          if (response.status === 400) {
            setSearchResults([]);
            setShowSearchResults(false);
            return;
          }
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }

        const data = await response.json();
        console.log("✅ Search results received:", data);

        setSearchResults(data.results || []);
        setShowSearchResults(true);
      } catch (error) {
        console.error("❌ Failed to search:", error);
        setSearchResults([]);
      } finally {
        setSearchLoading(false);
      }
    },
    [serverUrl],
  );

  // Debounce search input
  const handleSearchInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const query = e.target.value;
    setSearchQuery(query);

    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    debounceTimer.current = setTimeout(() => {
      performSearch(query);
    }, 300);
  };

  // Load full player data by character name
  const loadPlayerData = async (characterName: string) => {
    setPlayerLoading(true);
    try {
      const url = `${serverUrl}/api/hiscores/player/${encodeURIComponent(characterName)}`;
      console.log("📊 Loading player data:", url);
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      const playerData = await response.json();
      console.log("✅ Player data received:", playerData);
      console.log("🎨 Skills with colors:", playerData.skills);

      setSelectedPlayer({
        characterName: playerData.characterName,
        accountUsername: playerData.accountUsername,
        fleetTag: playerData.fleetTag,
        titleName: playerData.titleName,
        level: playerData.level,
        totalLevel: playerData.totalLevel,
        totalXp: playerData.totalXp,
        wealth: playerData.wealth,
        skills: playerData.skills || {},
        rank: playerData.rank,
      });
    } catch (error) {
      console.error("❌ Failed to fetch player data:", error);
      setSelectedPlayer(null);
    } finally {
      setPlayerLoading(false);
    }
  };

  // Select a result from autocomplete and fetch full player data
  const handleSelectSearchResult = async (result: SearchResult) => {
    await loadPlayerData(result.characterName);
    setSearchQuery("");
    setShowSearchResults(false);
    setSearchResults([]);
  };

  // Close search results when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        searchContainerRef.current &&
        !searchContainerRef.current.contains(e.target as Node)
      ) {
        setShowSearchResults(false);
      }
    };

    if (showSearchResults) {
      document.addEventListener("mousedown", handleClickOutside);
      return () =>
        document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [showSearchResults]);

  const groupedCategories = CATEGORIES.reduce(
    (acc, cat) => {
      if (!acc[cat.group]) acc[cat.group] = [];
      acc[cat.group].push(cat);
      return acc;
    },
    {} as Record<string, typeof CATEGORIES>,
  );

  const getCategoryInfo = () => {
    const cat = CATEGORIES.find((c) => c.id === category);
    return cat || { label: "Overall" };
  };

  const categoryInfo = getCategoryInfo();

  const formatNumber = (num: number | undefined): string => {
    if (num === undefined) return "—";
    return num.toLocaleString();
  };

  const handleRowClick = (entry: HiscoreEntry) => {
    loadPlayerData(entry.characterName);
  };

  return (
    <div className="hiscores-container">
      {/* Header */}
      <div className="hiscores-header">
        <div className="hiscores-header-content">
          <div>
            <h1 className="hiscores-title">Hiscores</h1>
            <p className="hiscores-subtitle">
              Track your ranking across all skills and achievements
            </p>
          </div>
          {onBackToGame && (
            <button onClick={onBackToGame} className="hiscores-back-button">
              Back to Game
            </button>
          )}
        </div>
      </div>

      {/* Main Container */}
      <div className="hiscores-main-container">
        {/* Sidebar - Category Navigation */}
        <div className="hiscores-sidebar">
          <div className="hiscores-sidebar-content">
            <div className="hiscores-sidebar-title">Categories</div>

            {Object.entries(groupedCategories).map(([group, categories]) => (
              <div key={group} className="hiscores-category-group">
                <div className="hiscores-group-label">{group}</div>
                {categories.map((cat) => (
                  <button
                    key={cat.id}
                    onClick={() => {
                      setCategory(cat.id);
                      setPage(1);
                      setSelectedPlayer(null);
                    }}
                    className={`hiscores-category-button ${
                      category === cat.id ? "active" : ""
                    }`}
                  >
                    {cat.label}
                  </button>
                ))}
              </div>
            ))}
          </div>
        </div>

        {/* Main Content */}
        <div className="hiscores-main-content">
          <div className="hiscores-content-area">
            {/* Show player overview or table */}
            {playerLoading ? (
              <div className="hiscores-loading-container">
                <div className="hiscores-spinner" />
                <p className="hiscores-loading-text">Loading player data...</p>
              </div>
            ) : selectedPlayer ? (
              // Player Overview View
              <div className="hiscores-player-overview">
                {/* Player Header */}
                <div className="hiscores-overview-header">
                  <div className="hiscores-overview-player-info">
                    <h2 className="hiscores-overview-name">
                      {selectedPlayer.characterName}
                    </h2>
                    {selectedPlayer.accountUsername && (
                      <p className="hiscores-overview-username">
                        @{selectedPlayer.accountUsername}
                      </p>
                    )}
                    {selectedPlayer.fleetTag && (
                      <p className="hiscores-overview-fleet-tag">
                        [{selectedPlayer.fleetTag}]
                      </p>
                    )}
                  </div>
                  <button
                    className="hiscores-close-button"
                    onClick={() => setSelectedPlayer(null)}
                    aria-label="Close details"
                  >
                    ✕
                  </button>
                </div>

                {selectedPlayer.titleName && (
                  <div className="hiscores-overview-title-badge">
                    {selectedPlayer.titleName}
                  </div>
                )}

                {/* Overview Content */}
                <div className="hiscores-overview-content">
                  {/* Stats Section */}
                  <div className="hiscores-overview-stats-section">
                    <h3 className="hiscores-overview-section-title">
                      Overall Stats
                    </h3>
                    <div className="hiscores-overview-stats-grid">
                      <div className="hiscores-overview-stat-item">
                        <span className="hiscores-overview-stat-label">
                          Total Level
                        </span>
                        <span className="hiscores-overview-stat-value">
                          {selectedPlayer.totalLevel}
                        </span>
                      </div>
                      <div className="hiscores-overview-stat-item">
                        <span className="hiscores-overview-stat-label">
                          Total XP
                        </span>
                        <span className="hiscores-overview-stat-value-xp">
                          {formatNumber(selectedPlayer.totalXp)}
                        </span>
                      </div>
                      <div className="hiscores-overview-stat-item">
                        <span className="hiscores-overview-stat-label">
                          Wealth
                        </span>
                        <span className="hiscores-overview-stat-value-wealth">
                          {formatNumber(selectedPlayer.wealth)}₱
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Skills Section */}
                  {selectedPlayer.skills &&
                    Object.keys(selectedPlayer.skills).length > 0 && (
                      <div className="hiscores-overview-skills-section">
                        <h3 className="hiscores-overview-section-title">
                          Skills
                        </h3>
                        <div className="hiscores-overview-skills-grid">
                          {Object.entries(selectedPlayer.skills)
                            .filter(([skillName]) =>
                              IMPLEMENTED_SKILLS.includes(skillName),
                            )
                            .map(([skillName, skillData]) => (
                              <div
                                key={skillName}
                                className="hiscores-overview-skill-card"
                                style={{
                                  borderLeftColor: skillData.color || "#58a6ff",
                                }}
                              >
                                <div className="hiscores-overview-skill-name">
                                  {skillName}
                                </div>
                                <div className="hiscores-overview-skill-level">
                                  Level {skillData.level}
                                </div>
                                <div className="hiscores-overview-skill-xp">
                                  {formatNumber(skillData.totalXp)} XP
                                </div>
                              </div>
                            ))}
                        </div>
                      </div>
                    )}
                </div>
              </div>
            ) : (
              // Hiscores Table View (Default)
              <>
                <div className="hiscores-title-bar">
                  <h2 className="hiscores-content-title">
                    {categoryInfo.label}
                  </h2>
                  <p className="hiscores-content-subtitle">
                    {totalCount.toLocaleString()} players tracked
                  </p>
                </div>

                {loading ? (
                  <div className="hiscores-loading-container">
                    <div className="hiscores-spinner" />
                    <p className="hiscores-loading-text">Loading hiscores...</p>
                  </div>
                ) : entries.length === 0 ? (
                  <div className="hiscores-empty-state">
                    <p className="hiscores-empty-text">No entries found</p>
                    <p className="hiscores-empty-subtext">
                      Try selecting a different category
                    </p>
                  </div>
                ) : (
                  <>
                    <div className="hiscores-table-container">
                      <table className="hiscores-table">
                        <thead className="hiscores-table-header">
                          <tr>
                            <th
                              className="hiscores-table-header-cell"
                              style={{ width: "60px" }}
                            >
                              Rank
                            </th>
                            <th
                              className="hiscores-table-header-cell"
                              style={{ flex: 1 }}
                            >
                              Player
                            </th>
                            <th
                              className="hiscores-table-header-cell"
                              style={{ width: "120px" }}
                            >
                              Level
                            </th>
                            <th
                              className="hiscores-table-header-cell"
                              style={{ width: "150px" }}
                            >
                              {category === "wealth" ? "Wealth" : "Experience"}
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {entries.map((entry, index) => (
                            <tr
                              key={`${entry.characterName}-${index}`}
                              className="hiscores-table-row"
                              onClick={() => handleRowClick(entry)}
                            >
                              <td className="hiscores-rank-cell">
                                <span className="hiscores-rank-number">
                                  #{entry.rank}
                                </span>
                              </td>
                              <td className="hiscores-player-cell">
                                <div className="hiscores-player-name">
                                  {entry.characterName}
                                </div>
                                {entry.accountUsername && (
                                  <div className="hiscores-player-username">
                                    @{entry.accountUsername}
                                  </div>
                                )}
                              </td>
                              <td className="hiscores-data-cell">
                                {category === "overall" || category === "wealth"
                                  ? formatNumber(entry.totalLevel)
                                  : formatNumber(entry.level)}
                              </td>
                              <td className="hiscores-data-cell">
                                {category === "wealth"
                                  ? `${formatNumber(entry.wealth)}₱`
                                  : formatNumber(
                                      category === "overall"
                                        ? entry.totalXp
                                        : entry.xp,
                                    )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    {/* Pagination */}
                    {totalPages > 1 && (
                      <div className="hiscores-pagination-container">
                        <button
                          onClick={() => setPage(Math.max(1, page - 1))}
                          disabled={page === 1}
                          className="hiscores-pagination-button"
                        >
                          Previous
                        </button>
                        <span className="hiscores-page-info">
                          Page{" "}
                          <span className="hiscores-page-number">{page}</span>{" "}
                          of{" "}
                          <span className="hiscores-page-number">
                            {totalPages}
                          </span>
                        </span>
                        <button
                          onClick={() =>
                            setPage(Math.min(totalPages, page + 1))
                          }
                          disabled={page === totalPages}
                          className="hiscores-pagination-button"
                        >
                          Next
                        </button>
                      </div>
                    )}
                  </>
                )}
              </>
            )}
          </div>
        </div>

        {/* Right Panel - Search & Player Details */}
        <div className="hiscores-right-panel">
          {/* Search Section */}
          <div className="hiscores-search-section" ref={searchContainerRef}>
            <h3 className="hiscores-search-title">Search Player</h3>
            <form
              className="hiscores-search-form"
              onSubmit={(e) => {
                e.preventDefault();
                performSearch(searchQuery);
              }}
            >
              <div style={{ flex: 1, position: "relative" }}>
                <input
                  ref={searchInputRef}
                  type="text"
                  className="hiscores-search-input"
                  placeholder="Character name..."
                  value={searchQuery}
                  onChange={handleSearchInputChange}
                  onFocus={() => {
                    if (searchQuery.length >= 2 && searchResults.length > 0) {
                      setShowSearchResults(true);
                    }
                  }}
                  autoComplete="off"
                />

                {/* Autocomplete Dropdown */}
                {showSearchResults && (
                  <div className="hiscores-autocomplete-dropdown">
                    {searchLoading ? (
                      <div className="hiscores-autocomplete-item disabled">
                        <span>Searching...</span>
                      </div>
                    ) : searchResults.length === 0 ? (
                      <div className="hiscores-autocomplete-item disabled">
                        <span>No results found</span>
                      </div>
                    ) : (
                      searchResults.map((result, idx) => (
                        <button
                          key={idx}
                          className="hiscores-autocomplete-item"
                          onClick={() => handleSelectSearchResult(result)}
                          type="button"
                        >
                          <div className="hiscores-autocomplete-name">
                            {result.characterName}
                          </div>
                          <div className="hiscores-autocomplete-details">
                            {result.accountUsername && (
                              <span>@{result.accountUsername}</span>
                            )}
                            {result.fleetTag && (
                              <span>[{result.fleetTag}]</span>
                            )}
                            <span
                              style={{ marginLeft: "auto", color: "#58a6ff" }}
                            >
                              → View
                            </span>
                          </div>
                        </button>
                      ))
                    )}
                  </div>
                )}
              </div>
              <button
                type="submit"
                className="hiscores-search-button"
                disabled={searchLoading || searchQuery.length < 2}
              >
                Search
              </button>
            </form>
          </div>

          {/* Empty state when not viewing player or when viewing table */}
          {!selectedPlayer && (
            <div className="hiscores-player-details-container">
              <div className="hiscores-empty-state">
                <p className="hiscores-empty-text">No player selected</p>
                <p className="hiscores-empty-subtext">
                  Click a row to view details or search above
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
